import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { sfx } from "../lib/sound";

/**
 * Fires a burst of gold coins that fly from the screen centre into the
 * coins HUD pill (`[data-testid="hud-coins"]`), giving reward moments a
 * physical "collected" feel. Purely decorative — the coin balance is
 * updated separately by the caller.
 *
 * Props:
 *   fire   — a monotonically increasing trigger (number). Each new value
 *            re-fires the burst. 0 / unchanged = idle.
 *   count  — number of coins (default 12)
 *   targetSelector — element to fly towards (default the HUD coins pill)
 */
export default function CoinBurst({
  fire = 0,
  count = 12,
  targetSelector = '[data-testid="hud-coins"]',
}) {
  const [coins, setCoins] = useState([]);

  useEffect(() => {
    if (!fire) return;
    const target = document.querySelector(targetSelector);
    const tr = target?.getBoundingClientRect();
    const tx = tr ? tr.left + tr.width / 2 : window.innerWidth - 40;
    const ty = tr ? tr.top + tr.height / 2 : 40;
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;

    const items = Array.from({ length: count }).map((_, i) => ({
      id: `${fire}_${i}`,
      sx: cx + (Math.random() * 120 - 60),
      sy: cy + (Math.random() * 120 - 60),
      // slight arc apex between start and target
      mx: (cx + tx) / 2 + (Math.random() * 80 - 40),
      my: Math.min(cy, ty) - 60 - Math.random() * 60,
      tx,
      ty,
      delay: i * 0.04,
      dur: 0.7 + Math.random() * 0.25,
      size: 18 + Math.round(Math.random() * 8),
    }));
    setCoins(items);
    try { sfx.coinCash?.(); } catch (_) { /* ignore */ }

    const clear = setTimeout(() => setCoins([]), 1500);
    return () => clearTimeout(clear);
  }, [fire, count, targetSelector]);

  return (
    <div className="fixed inset-0 z-[240] pointer-events-none" data-testid="coin-burst-layer" aria-hidden="true">
      <AnimatePresence>
        {coins.map((c) => (
          <motion.div
            key={c.id}
            className="absolute"
            style={{ left: 0, top: 0, width: c.size, height: c.size }}
            initial={{ x: c.sx, y: c.sy, opacity: 0, scale: 0.4 }}
            animate={{
              x: [c.sx, c.mx, c.tx],
              y: [c.sy, c.my, c.ty],
              opacity: [0, 1, 1, 0.9, 0],
              scale: [0.4, 1, 1, 0.75, 0.5],
              rotate: [0, 180, 360],
            }}
            transition={{ duration: c.dur, delay: c.delay, ease: "easeInOut", times: [0, 0.15, 1] }}
          >
            <div
              className="w-full h-full rounded-full grid place-items-center"
              style={{
                background: "radial-gradient(circle at 35% 30%, #FBE3A6 0%, #F0B24E 45%, #C9812C 100%)",
                boxShadow: "0 2px 6px rgba(120,72,12,0.45), inset 0 1px 1px rgba(255,255,255,0.6)",
                border: "1px solid rgba(160,100,24,0.5)",
              }}
            >
              <span className="text-[10px] font-black text-[#8A5410] leading-none" style={{ textShadow: "0 1px 0 rgba(255,255,255,0.4)" }}>$</span>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
